import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Search, Loader2, Trash2, RotateCcw, Mail, MapPin, Calendar,
  Filter, ChevronDown,
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { api } from '../../lib/api';

/**
 * RejectedCandidatesPage — "Poubelle" administrative.
 *
 * Liste les candidats rejetés (manuels + auto) avec filtres et bouton
 * Restaurer (qui repasse le candidat à `analyzed` pour réévaluation).
 */
const PAGE_TITLE = 'Candidats rejetés';

const CATEGORIES = [
  { value: '', label: 'Toutes catégories' },
  { value: 'assistant_virtuel', label: 'Assistant Virtuel' },
  { value: 'community_manager', label: 'Community Manager' },
  { value: 'gestion_ads', label: 'Gestion Ads / Traffic Manager' },
  { value: 'seo', label: 'SEO' },
  { value: 'developpeur', label: 'Développeur / Tech' },
  { value: 'graphiste', label: 'Graphiste' },
  { value: 'redacteur', label: 'Rédacteur' },
  { value: 'comptabilite', label: 'Comptabilité' },
  { value: 'support_client', label: 'Support Client' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'autre', label: 'Autre' },
];

const RejectedCandidatesPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);

  // Filters
  const [type, setType] = useState('all'); // all | manual | auto
  const [country, setCountry] = useState('');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Country list (from candidates)
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    api.get('/countries').then(async (res) => {
      if (res.ok) {
        const data = await res.json();
        setCountries(data.countries || []);
      }
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('type', type);
      if (country) params.append('country', country);
      if (category) params.append('category', category);
      if (search.trim()) params.append('search', search.trim());
      params.append('limit', '100');

      const res = await api.get(`/admin/candidates/rejected?${params}`);
      if (res.ok) {
        const data = await res.json();
        setItems(data.candidates || []);
        setTotal(data.total || 0);
      } else {
        toast.error('Erreur de chargement des rejetés');
      }
    } catch {
      toast.error('Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, [type, country, category, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRestore = async (cand) => {
    if (!window.confirm(`Restaurer ${cand.first_name} ${cand.last_name} ?\n\nLe candidat reviendra dans la liste Candidats au statut "Analysé" pour réévaluation.`)) {
      return;
    }
    setRestoringId(cand.id);
    try {
      const res = await api.post(`/admin/candidates/${cand.id}/restore`);
      if (res.ok) {
        toast.success(`${cand.first_name} restauré · à réévaluer`);
        setItems((prev) => prev.filter((x) => x.id !== cand.id));
        setTotal((t) => Math.max(0, t - 1));
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Échec de la restauration');
      }
    } catch {
      toast.error('Erreur réseau');
    } finally {
      setRestoringId(null);
    }
  };

  const counts = useMemo(() => {
    const m = items.filter((c) => c.is_manual).length;
    return { manual: m, auto: items.length - m };
  }, [items]);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="rejected-candidates-page">
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-2xl font-display font-semibold text-tp-text flex items-center gap-2">
          <Trash2 className="h-6 w-6 text-red-500" />
          {PAGE_TITLE}
        </h1>
        <p className="text-sm text-tp-text-light mt-1">
          {total} candidat{total > 1 ? 's' : ''} rejeté{total > 1 ? 's' : ''}
          {' · '}
          <span className="text-gray-500">{counts.manual} manuel{counts.manual > 1 ? 's' : ''} · {counts.auto} auto</span>
        </p>
      </div>

      {/* Type tabs */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="rejected-type-tabs">
        {[
          { id: 'all', label: 'Tous' },
          { id: 'manual', label: 'Manuel' },
          { id: 'auto', label: 'Auto' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setType(t.id)}
            className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
              type === t.id
                ? 'bg-tp-primary text-white border-tp-primary'
                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
            }`}
            data-testid={`rejected-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search + filters */}
      <Card className="p-4 mb-5 border-tp-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher (nom, email, profession...)"
            className="pl-9"
            data-testid="rejected-search-input"
          />
        </div>
        <button
          onClick={() => setShowFilters((v) => !v)}
          className="mt-3 inline-flex items-center gap-1.5 text-xs text-tp-primary hover:underline"
          data-testid="rejected-filters-toggle"
        >
          <Filter className="h-3.5 w-3.5" />
          Filtres avancés
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </button>
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-100" data-testid="rejected-filters-panel">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Pays</label>
              <Select value={country || '__all__'} onValueChange={(v) => setCountry(v === '__all__' ? '' : v)}>
                <SelectTrigger data-testid="rejected-filter-country">
                  <SelectValue placeholder="Tous pays" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tous pays</SelectItem>
                  {countries.map((c) => (
                    <SelectItem key={c.code || c} value={c.code || c}>{c.name || c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Catégorie</label>
              <Select value={category || '__all__'} onValueChange={(v) => setCategory(v === '__all__' ? '' : v)}>
                <SelectTrigger data-testid="rejected-filter-category">
                  <SelectValue placeholder="Toutes catégories" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value || '__all__'} value={c.value || '__all__'}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </Card>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-tp-text-light" />
        </div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center border-tp-border" data-testid="rejected-empty-state">
          <Trash2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-tp-text-light">Aucun candidat rejeté pour ces filtres.</p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="rejected-list">
          {items.map((c) => (
            <RejectedRow
              key={c.id}
              candidate={c}
              restoring={restoringId === c.id}
              onRestore={() => handleRestore(c)}
              onView={() => navigate(`/admin/candidates/${c.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const RejectedRow = ({ candidate, onRestore, onView, restoring }) => {
  const fullName = `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() || 'Candidat';
  const rejectedDate = candidate.rejected_at ? new Date(candidate.rejected_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <Card
      className="p-3 md:p-4 border border-gray-200 hover:border-gray-300 transition-colors flex flex-col md:flex-row md:items-center gap-3 md:gap-4"
      data-testid={`rejected-row-${candidate.id}`}
    >
      <div className="flex items-center gap-2 flex-shrink-0">
        <Badge
          className={`text-[10px] uppercase tracking-wide ${
            candidate.is_manual ? 'bg-red-500 text-white' : 'bg-gray-400 text-white'
          }`}
        >
          {candidate.is_manual ? 'Manuel' : 'Auto'}
        </Badge>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-tp-text truncate">{fullName}</div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-gray-500 mt-0.5">
          {candidate.main_profession && <span className="truncate">{candidate.main_profession}</span>}
          {candidate.country && (
            <span className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{candidate.country}</span>
          )}
          {candidate.email && (
            <span className="inline-flex items-center gap-0.5 truncate max-w-[220px]"><Mail className="h-3 w-3" />{candidate.email}</span>
          )}
        </div>
        {candidate.rejection_reason && (
          <div className="text-[11px] text-gray-400 mt-1 italic truncate">
            Motif : {candidate.rejection_reason}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-[11px] text-gray-400 hidden md:inline-flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {rejectedDate}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={onView}
          className="h-8 text-xs"
          data-testid={`rejected-view-${candidate.id}`}
        >
          Voir
        </Button>
        <Button
          size="sm"
          onClick={onRestore}
          disabled={restoring}
          className="h-8 text-xs bg-tp-accent hover:bg-tp-accent/90 text-white"
          data-testid={`rejected-restore-${candidate.id}`}
        >
          {restoring ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3 mr-1" />}
          Restaurer
        </Button>
      </div>
    </Card>
  );
};

export default RejectedCandidatesPage;
